// src/pages/AntaresLanding.tsx
import {
  ArrowRight,
  Award,
  BadgeCheck,
  Bot,
  BriefcaseBusiness,
  CloudCog,
  Code2,
  Cpu,
  Layers,
  Mail,
  MessageCircle,
  Phone,
  Rocket,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AntaresLogo from '../components/AntaresLogo';
import CookieConsent from '../components/CookieConsent';
import AntaresAIChat from '../components/AntaresAIChat';

const BRAND_NAME = 'Antares Tecnologia GTA';
const CONTACT_EMAIL = 'contato@antarestecnologiagta.com.br';
const CONTACT_PHONE_DISPLAY = '+55 (11) 93062-8624';

// Importante (Meta/WhatsApp Business): a razão social precisa estar VISÍVEL no site.
const LEGAL_NAME = 'Antares Tecnologia';
const LEGAL_CNPJ = '18.987.440/0001-40';
const LEGAL_RAZAO_SOCIAL = 'THIAGO XAVIER PACHECO 32629393828';

const WHATSAPP =
  'https://wa.me/5511930628624?text=Ol%C3%A1!%20Gostaria%20de%20conhecer%20mais%20sobre%20as%20solu%C3%A7%C3%B5es%20da%20Antares%20Tecnologia.';

// O que fazemos - Serviços principais
const services = [
  {
    title: 'Automação e Inteligência para Negócios',
    desc: 'Automatize atendimentos, agendamentos e processos com soluções integradas ao WhatsApp e outras plataformas. Seu negócio funcionando 24/7 sem depender de equipe operacional.',
    icon: Bot,
    bullets: [
      'Atendimento automático para clientes',
      'Agendamento inteligente (salões, clínicas, serviços)',
      'Bots personalizados para vendas e suporte',
      'Integração com APIs e sistemas externos',
    ],
  },
  {
    title: 'Sistemas Sob Medida',
    desc: 'Desenvolvemos sistemas personalizados de acordo com a necessidade real do seu negócio. Do planejamento ao deploy, entregamos software que escala com a sua operação.',
    icon: CloudCog,
    bullets: [
      'Plataformas web e SaaS',
      'Dashboards e painéis administrativos',
      'Sistemas de gestão interna',
      'Landing pages profissionais e integradas',
    ],
  },
  {
    title: 'Escala, Performance e Segurança',
    desc: 'Criamos soluções preparadas para crescer junto com sua empresa, sem perder velocidade, estabilidade ou controle.',
    icon: Rocket,
    bullets: [
      'Sistemas rápidos e estáveis',
      'Arquitetura moderna e escalável',
      'Integrações robustas com terceiros',
      'Foco em segurança e confiabilidade',
    ],
  },
];

// Nossos Produtos
const products = [
  {
    name: 'ZapBeauty',
    tagline: 'Automação inteligente para salões de beleza',
    description: 'Sistema completo de agendamentos, lembretes automáticos e fidelização de clientes via WhatsApp. Aumente a produtividade do seu salão e elimine o no-show.',
    icon: Sparkles,
    features: [
      'Agendamento automático via WhatsApp',
      'Lembretes de consulta personalizados',
      'Gestão de clientes e histórico completo',
      'Integração com WhatsApp Business API',
    ],
    color: 'from-purple-500 to-pink-500',
  },
  {
    name: 'Organizei',
    tagline: 'Plataforma de gestão pessoal e financeira',
    description: 'Solução digital completa para organização financeira e pessoal. Controle de gastos, metas, relatórios e gestão de rotina em uma única plataforma intuitiva.',
    icon: Target,
    features: [
      'Controle financeiro pessoal e corporativo',
      'Gestão de metas e produtividade',
      'Relatórios e dashboards personalizados',
      'Aplicativo mobile e plataforma web',
    ],
    color: 'from-blue-500 to-cyan-500',
  },
  {
    name: 'Organizei Petshop',
    tagline: 'Gestão completa para petshops e clínicas veterinárias',
    description: 'Sistema especializado para gestão de petshops: agendamentos, prontuários de pets, controle de estoque, assinatura premium e integração com Stripe.',
    icon: Award,
    features: [
      'Agendamento de banho, tosa e consultas',
      'Prontuário digital para cada pet',
      'Planos de assinatura e fidelização',
      'Integração com pagamentos (Stripe)',
    ],
    color: 'from-green-500 to-teal-500',
  },
  {
    name: 'Pedido Balcão',
    tagline: 'Sistema de pedidos para restaurantes e lanchonetes',
    description: 'Plataforma de pedidos digitais para estabelecimentos food service. Cardápio digital, gestão de pedidos em tempo real e integração com cozinha.',
    icon: Layers,
    features: [
      'Cardápio digital para o cliente',
      'Pedidos em tempo real para a cozinha',
      'Gestão de mesas e atendimentos',
      'Aplicativo mobile integrado',
    ],
    color: 'from-orange-500 to-red-500',
  },
  {
    name: 'FoodGenie',
    tagline: 'Inteligência artificial aplicada ao food service',
    description: 'Solução com IA para otimizar operações de alimentação: sugestões inteligentes de cardápio, análise de preferências e automação de pedidos.',
    icon: Cpu,
    features: [
      'Recomendações de cardápio com IA',
      'Análise de preferências de clientes',
      'Automação de pedidos recorrentes',
      'Relatórios de performance',
    ],
    color: 'from-yellow-500 to-orange-500',
  },
  {
    name: 'FlashBiz AI',
    tagline: 'Inteligência artificial para pequenos negócios',
    description: 'Plataforma que usa IA para ajudar pequenos negócios a tomar melhores decisões: análise de dados, automação de processos e insights de crescimento.',
    icon: Zap,
    features: [
      'Análise inteligente de dados do negócio',
      'Automação de processos repetitivos',
      'Insights e sugestões de crescimento',
      'Integração com plataformas existentes',
    ],
    color: 'from-indigo-500 to-purple-500',
  },
];

// Portfólio de áreas de atuação
const portfolioAreas = [
  {
    area: 'Saúde e Bem-estar',
    desc: 'Sistemas de agendamento, prontuários e gestão para clínicas, salões e petshops.',
    icon: Sparkles,
  },
  {
    area: 'Alimentação e Varejo',
    desc: 'Plataformas de pedidos, cardápio digital e gestão operacional para food service.',
    icon: BriefcaseBusiness,
  },
  {
    area: 'E-commerce e Afiliados',
    desc: 'Automação de marketing de afiliados, integração com Amazon e geração de conteúdo em escala.',
    icon: TrendingUp,
  },
  {
    area: 'Finanças e Gestão',
    desc: 'Soluções de controle financeiro, organização e produtividade para pessoas e empresas.',
    icon: Star,
  },
  {
    area: 'Inteligência Artificial',
    desc: 'Ferramentas com IA para análise de dados, automação inteligente e insights de negócio.',
    icon: Cpu,
  },
  {
    area: 'Automação e Conteúdo',
    desc: 'Criação automatizada de vídeos, shorts e conteúdo para redes sociais em escala.',
    icon: Code2,
  },
];

// Por que escolher a Antares
const differentials = [
  {
    title: 'Visão de Negócio, Não Apenas Técnica',
    description: 'Entendemos o seu processo antes de escrever a primeira linha de código. Entregamos soluções que fazem sentido para a sua operação.',
    icon: Target,
  },
  {
    title: 'Integrações Oficiais e Seguras',
    description: 'Trabalhamos apenas com canais oficiais (WhatsApp Business API, Twilio, 360dialog). Seu negócio protegido e dentro das regras.',
    icon: ShieldCheck,
  },
  {
    title: 'Foco em Aumento de Vendas e Produtividade',
    description: 'Automação que escala vendas, reduz custos operacionais e melhora a experiência do cliente. Resultados mensuráveis.',
    icon: TrendingUp,
  },
  {
    title: 'Atendimento Consultivo e Personalizado',
    description: 'Não vendemos pacote genérico. Analisamos o seu cenário e construímos a solução certa para o seu momento.',
    icon: Users,
  },
  {
    title: 'Experiência com Sistemas Reais e Escaláveis',
    description: 'Mais de 15 anos construindo soluções para setores exigentes: saúde, financeiro, telecomunicações e varejo.',
    icon: Award,
  },
  {
    title: 'Uso de Tecnologias Modernas com IA',
    description: 'Incorporamos Inteligência Artificial nos nossos produtos para entregar automação mais inteligente e decisões mais rápidas.',
    icon: Cpu,
  },
];

function Section({ id, title, subtitle, children }: { id?: string; title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-brand-text">
            {title}
          </h2>
          {subtitle ? <p className="mt-3 text-brand-muted max-w-3xl">{subtitle}</p> : null}
        </div>
        {children}
      </div>
    </section>
  );
}

export default function AntaresLanding() {
  const year = useMemo(() => new Date().getFullYear(), []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <main className="min-h-screen overflow-x-hidden bg-white text-brand-text">
      <CookieConsent />

      {/* Hero dark section: navbar + hero com background image */}
      <div
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#0d0b1e 0%,#1a0e3a 40%,#0e1a3a 100%)' }}
      >
        {/* Background image */}
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/backgroundLandingPage.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            opacity: 0.55,
          }}
        />
        {/* Overlay suave para leitura */}
        <div className="pointer-events-none absolute inset-0 z-0" style={{ background: 'linear-gradient(to bottom, rgba(10,6,30,0.35) 0%, rgba(10,6,30,0.6) 100%)' }} />

      {/* Navbar */}
      <header className="relative z-50 border-b border-white/10 overflow-visible">
        <div className="mx-auto max-w-7xl px-6 py-2 flex items-center justify-between overflow-visible">
          <a href="#top" className="flex items-center overflow-visible">
            <AntaresLogo className="h-28 sm:h-36 w-auto drop-shadow-lg" style={{ marginTop: '-10px', marginBottom: '-10px' }} />
          </a>

          <nav className="hidden md:flex items-center gap-6 text-sm text-white/80">
            <a href="#servicos" className="hover:text-white transition font-medium">Serviços</a>
            <a href="#produtos" className="hover:text-white transition font-medium">Produtos</a>
            <a href="#experiencia" className="hover:text-white transition font-medium">Portfólio</a>
            <a href="#diferenciais" className="hover:text-white transition font-medium">Diferenciais</a>
            <a href="#faq" className="hover:text-white transition font-medium">Dúvidas</a>
            <a href="#contato" className="hover:text-white transition font-medium">Contato</a>
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20 transition"
              aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
              onClick={() => setMobileMenuOpen((v) => !v)}
            >
              Menu
            </button>

            <a
              href={WHATSAPP}
              className="hidden sm:inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold bg-gradient-to-r from-brand-primary to-brand-secondary text-white hover:brightness-110 transition shadow-[0_4px_20px_rgba(124,58,237,0.3)]"
            >
              <MessageCircle className="h-4 w-4" />
              Falar no WhatsApp
            </a>
          </div>
        </div>

        {mobileMenuOpen ? (
          <div className="md:hidden border-t border-white/10 bg-black/40 backdrop-blur">
            <div className="mx-auto max-w-7xl px-6 py-4 flex flex-col gap-3 text-sm text-white/80">
              <a onClick={() => setMobileMenuOpen(false)} href="#servicos" className="hover:text-white transition font-medium">Serviços</a>
              <a onClick={() => setMobileMenuOpen(false)} href="#produtos" className="hover:text-white transition font-medium">Produtos</a>
              <a onClick={() => setMobileMenuOpen(false)} href="#experiencia" className="hover:text-white transition font-medium">Portfólio</a>
              <a onClick={() => setMobileMenuOpen(false)} href="#diferenciais" className="hover:text-white transition font-medium">Diferenciais</a>
              <a onClick={() => setMobileMenuOpen(false)} href="#faq" className="hover:text-white transition font-medium">Dúvidas</a>
              <a onClick={() => setMobileMenuOpen(false)} href="#contato" className="hover:text-white transition font-medium">Contato</a>
              <a
                onClick={() => setMobileMenuOpen(false)}
                href={WHATSAPP}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold bg-gradient-to-r from-brand-primary to-brand-secondary text-white hover:brightness-110 transition"
              >
                <MessageCircle className="h-4 w-4" />
                Falar no WhatsApp
              </a>
            </div>
          </div>
        ) : null}
      </header>

      {/* Hero */}
      <section id="top" className="relative z-10 pt-14 sm:pt-18 pb-16 sm:pb-24">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs text-white/80">
              <BadgeCheck className="h-4 w-4 text-brand-primary" />
              <span>+15 anos transformando negócios com tecnologia</span>
            </div>

            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
              Transformamos tecnologia
              <span className="block bg-clip-text text-transparent bg-gradient-to-r from-brand-primary via-brand-accent to-brand-secondary">
                em crescimento real para o seu negócio
              </span>
            </h1>

            <p className="mt-5 text-lg text-white/75 max-w-xl">
              Automação, sistemas sob medida e soluções inteligentes para empresas que querem vender mais, ganhar eficiência e escalar com segurança.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={WHATSAPP}
                className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold bg-gradient-to-r from-brand-primary to-brand-secondary text-white hover:brightness-110 transition shadow-[0_4px_20px_rgba(124,58,237,0.3)]"
              >
                Solicitar avaliação gratuita
                <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href="#produtos"
                className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold border border-white/30 bg-white/10 hover:bg-white/20 text-white transition"
              >
                Ver produtos
              </a>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <TrustChip icon={ShieldCheck} title="Oficial" desc="Canais aprovados" />
              <TrustChip icon={Target} title="Sob medida" desc="Para seu processo" />
              <TrustChip icon={TrendingUp} title="Resultado" desc="Foco em crescimento" />
            </div>
          </div>

          <div className="relative">
            <div className="rounded-2xl border border-brand-border bg-white p-6 sm:p-8 shadow-[0_8px_40px_rgba(124,58,237,0.1)]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-brand-muted">Resumo rápido</p>
                  <p className="text-xl font-bold text-brand-text">O que você ganha</p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-brand-primary via-brand-accent to-brand-secondary opacity-90" />
              </div>
              <div className="mt-6 grid gap-3">
                <BenefitLine>Atendimento 24/7 com automação e handoff humano</BenefitLine>
                <BenefitLine>Sistemas sob medida para o seu processo</BenefitLine>
                <BenefitLine>Integrações (CRM, pagamentos, agendas, ERPs)</BenefitLine>
                <BenefitLine>Soluções com IA para decisões mais rápidas</BenefitLine>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <MiniStat label="Experiência" value="+15 anos" />
                <MiniStat label="Segmentos" value="6+ mercados" />
                <MiniStat label="Entrega" value="MVP rápido" />
                <MiniStat label="Suporte" value="Especializado" />
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>{/* fim hero dark section */}

      {/* Sobre Nós */}
      <section className="py-16 sm:py-20 border-t border-brand-border">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-widest text-brand-primary uppercase mb-3">Sobre nós</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-brand-text leading-snug">
                Mais de 15 anos ajudando negócios a crescerem com tecnologia
              </h2>
              <p className="mt-5 text-brand-muted leading-relaxed">
                Na Antares Tecnologia GTA, ajudamos empresas a sair do operacional manual e entrar em um novo nível de produtividade e crescimento.
              </p>
              <p className="mt-4 text-brand-muted leading-relaxed">
                Com mais de 15 anos de experiência no desenvolvimento de soluções digitais, atuamos na criação de sistemas, automações e plataformas inteligentes que resolvem problemas reais do dia a dia empresarial.
              </p>
              <p className="mt-4 text-brand-text font-medium leading-relaxed">
                Nosso foco não é apenas tecnologia — é resultado. Entendemos o seu negócio, identificamos oportunidades e entregamos soluções que realmente fazem diferença.
              </p>
              <a
                href={WHATSAPP}
                className="mt-7 inline-flex items-center gap-2 rounded-lg px-6 py-3 font-semibold bg-gradient-to-r from-brand-primary to-brand-secondary text-white hover:brightness-110 transition shadow-[0_4px_20px_rgba(124,58,237,0.3)]"
              >
                Falar com um especialista
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-brand-border bg-white p-6 flex flex-col gap-2 shadow-sm">
                <p className="text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-brand-primary to-brand-secondary">+15</p>
                <p className="text-sm text-brand-muted">Anos de experiência em tecnologia</p>
              </div>
              <div className="rounded-2xl border border-brand-border bg-white p-6 flex flex-col gap-2 shadow-sm">
                <p className="text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-brand-primary to-brand-secondary">+6</p>
                <p className="text-sm text-brand-muted">Segmentos de mercado atendidos</p>
              </div>
              <div className="rounded-2xl border border-brand-border bg-white p-6 flex flex-col gap-2 shadow-sm">
                <p className="text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-brand-primary to-brand-secondary">100%</p>
                <p className="text-sm text-brand-muted">Foco em resultado e crescimento</p>
              </div>
              <div className="rounded-2xl border border-brand-border bg-white p-6 flex flex-col gap-2 shadow-sm">
                <p className="text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-brand-primary to-brand-secondary">24/7</p>
                <p className="text-sm text-brand-muted">Automações funcionando sem parar</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* O que fazemos */}
      <Section id="servicos" title="O que fazemos" subtitle="Um pacote completo: estratégia, implementação e evolução contínua para o seu negócio crescer.">
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s) => (
            <div key={s.title} className="group rounded-2xl border border-brand-border bg-white p-6 hover:shadow-md transition-all duration-300 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="rounded-xl bg-brand-card border border-brand-border p-2">
                  <s.icon className="h-6 w-6 text-brand-primary" />
                </div>
                <h3 className="text-lg font-semibold text-brand-text">{s.title}</h3>
              </div>
              <p className="mt-2 text-sm text-brand-muted">{s.desc}</p>
              <ul className="mt-4 space-y-2 text-sm">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <BadgeCheck className="h-4 w-4 text-brand-primary mt-0.5 flex-shrink-0" />
                    <span className="text-brand-muted">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* Nossos Produtos */}
      <Section id="produtos" title="Nossos Produtos" subtitle="Soluções próprias (SaaS) desenvolvidas e operadas pela Antares para acelerar diferentes segmentos de negócio.">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <div key={product.name} className="group rounded-2xl border border-brand-border bg-white p-7 transition-all duration-300 shadow-sm hover:shadow-md relative overflow-hidden">
              <div className="absolute inset-0 opacity-[0.04]">
                <div className={`absolute inset-0 bg-gradient-to-br ${product.color}`} />
              </div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`rounded-2xl bg-gradient-to-br ${product.color} p-2.5 shadow-[0_4px_12px_rgba(0,0,0,0.15)]`}>
                    <product.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-brand-text">{product.name}</h3>
                    <p className="text-xs text-brand-muted">{product.tagline}</p>
                  </div>
                </div>
                <p className="mt-3 text-sm text-brand-muted leading-relaxed">{product.description}</p>
                <ul className="mt-5 space-y-2">
                  {product.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <BadgeCheck className="h-4 w-4 text-brand-primary mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-brand-text">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={WHATSAPP}
                  className="mt-6 inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold bg-brand-card border border-brand-border hover:bg-gray-100 text-brand-text transition"
                >
                  <MessageCircle className="h-4 w-4 text-brand-secondary" />
                  <span>Quero saber mais</span>
                  <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Portfólio / Experiência */}
      <Section
        id="experiencia"
        title="Experiência e Portfólio"
        subtitle="Ao longo da nossa trajetória, desenvolvemos soluções para empresas de grande porte e ambientes de alta exigência. Essa maturidade se reflete na qualidade do que entregamos para você."
      >
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {portfolioAreas.map((item) => (
            <div key={item.area} className="rounded-2xl border border-brand-border bg-white p-6 hover:shadow-md transition-all duration-300 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="rounded-xl bg-brand-card border border-brand-border p-3">
                  <item.icon className="h-6 w-6 text-brand-accent" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-brand-text mb-1">{item.area}</h3>
                  <p className="text-sm text-brand-muted leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-brand-primary/20 bg-brand-primary/5 p-6 sm:p-8">
          <p className="text-center text-brand-text font-medium text-lg leading-relaxed">
            🔥 Hoje já ajudamos negócios a automatizar atendimentos, organizar agendas e transformar o WhatsApp em canal de vendas.
          </p>
          <p className="mt-3 text-center text-sm text-brand-muted">
            Essa experiência nos permite trazer para o seu negócio o mesmo nível de qualidade, organização e eficiência utilizado em grandes empresas.
          </p>
        </div>
      </Section>

      {/* Por que escolher a Antares */}
      <Section id="diferenciais" title="Diferenciais" subtitle="Nada de improviso: visão de negócio, processo e execução de ponta a ponta.">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {differentials.map((diff) => (
            <div key={diff.title} className="rounded-2xl border border-brand-border bg-white p-6 hover:shadow-md transition-all duration-300 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="rounded-xl bg-brand-card border border-brand-border p-3">
                  <diff.icon className="h-6 w-6 text-brand-accent" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-brand-text mb-2">{diff.title}</h3>
                  <p className="text-sm text-brand-muted leading-relaxed">{diff.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Para quem é */}
      <Section
        id="para-quem"
        title="Para quem é"
        subtitle="Atendemos empresas que querem sair do operacional manual e crescer com tecnologia."
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: '💼', label: 'Pequenas e médias empresas que querem escalar' },
            { icon: '✂️', label: 'Salões de beleza, clínicas e serviços locais' },
            { icon: '🤖', label: 'Negócios que querem automatizar atendimento' },
            { icon: '💻', label: 'Empresas que precisam de sistemas próprios' },
            { icon: '🍔', label: 'Restaurantes e food service buscando eficiência' },
            { icon: '🚀', label: 'Empreendedores que querem escalar com tecnologia' },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3 rounded-2xl border border-brand-border bg-white p-5 hover:shadow-md transition-all duration-300 shadow-sm">
              <span className="text-2xl">{item.icon}</span>
              <p className="text-sm text-brand-text leading-relaxed">{item.label}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Como funciona */}
      <Section
        id="processo"
        title="Como funciona"
        subtitle="Um caminho claro do diagnóstico ao resultado (sem enrolação e com previsibilidade)."
      >
        <div className="grid md:grid-cols-4 gap-4">
          <StepCard step="01" title="Diagnóstico" desc="Entendemos seu funil, time e operação para desenhar o fluxo ideal." />
          <StepCard step="02" title="Arquitetura" desc="Templates, filas, integrações, governança e segurança." />
          <StepCard step="03" title="Implementação" desc="Bot, API, painéis e integrações — com testes e validação." />
          <StepCard step="04" title="Evolução" desc="Otimizações por métricas: conversão, tempo de resposta e custo." />
        </div>
      </Section>

      {/* FAQ */}
      <Section
        id="faq"
        title="Dúvidas comuns"
        subtitle="Respostas objetivas para acelerar sua decisão."
      >
        <div className="grid md:grid-cols-2 gap-6">
          <FaqItem
            q="Vocês desenvolvem sistemas do zero?"
            a="Sim. Desenvolvemos desde landing pages e sistemas simples até plataformas SaaS completas, sob medida para o seu processo e negócio."
          />
          <FaqItem
            q="Vocês fazem WhatsApp Business API oficial?"
            a="Sim. Trabalhamos com provedores oficiais (como Twilio e 360dialog), ajudando na integração, automação de atendimento e configuração de templates."
          />
          <FaqItem
            q="Dá para integrar com meu sistema atual?"
            a="Sim. Criamos integrações via APIs e webhooks com CRMs, ERPs, gateways de pagamento, agendas e qualquer sistema que possua API."
          />
          <FaqItem
            q="Posso contratar apenas automação ou precisa ser um projeto completo?"
            a="Você pode contratar pontualmente: automação, sistema, integração ou consultoria. Adaptamos o escopo à sua necessidade real."
          />
          <FaqItem
            q="Em quanto tempo consigo colocar no ar?"
            a="Depende do escopo, mas normalmente entregamos um MVP funcional rapidamente e evoluímos por sprints, com datas claras e previsibilidade."
          />
          <FaqItem
            q="Vocês atendem outros segmentos além de salões e restaurantes?"
            a="Sim. Já atuamos em saúde, financeiro, pet care, food service, e-commerce, afiliados e gestão pessoal. Nossa abordagem se adapta a qualquer negócio."
          />
        </div>
      </Section>

      {/* CTA final */}
      <Section id="contato" title="Pronto para o próximo nível?" subtitle="Vamos entender o seu cenário e mostrar como a tecnologia pode trabalhar a seu favor.">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-lg text-brand-muted mb-4">
            Seja automação via WhatsApp, desenvolvimento de sistema próprio ou integração com suas ferramentas —
            estamos prontos para transformar sua ideia em realidade.
          </p>
          <p className="text-base text-brand-text font-medium mb-8 rounded-2xl border border-brand-primary/20 bg-brand-primary/5 px-6 py-4">
            👉 Fale agora conosco e solicite uma avaliação gratuita
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href={WHATSAPP}
              className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-semibold bg-gradient-to-r from-brand-primary to-brand-secondary text-white hover:brightness-110 transition shadow-[0_4px_20px_rgba(124,58,237,0.3)]"
            >
              <MessageCircle className="h-5 w-5" />
              <span>Falar no WhatsApp</span>
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-semibold border border-brand-border bg-brand-card hover:bg-gray-100 text-brand-text transition"
            >
              <Mail className="h-5 w-5 text-brand-primary" />
              <span>Enviar e-mail</span>
            </a>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4 text-left">
            <ContactCard icon={Phone} title="Telefone" value={CONTACT_PHONE_DISPLAY} href={WHATSAPP} hint="Preferência: WhatsApp" />
            <ContactCard icon={Mail} title="E-mail" value={CONTACT_EMAIL} href={`mailto:${CONTACT_EMAIL}`} hint="Respondemos rápido" />
            <ContactCard icon={BadgeCheck} title="Atendimento" value="Seg–Sex" hint="Projetos e suporte" />
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="relative overflow-hidden mt-10" style={{ background: 'linear-gradient(135deg,#0d0b1e 0%,#1a0e3a 40%,#0e1a3a 100%)' }}>
        {/* Background image */}
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/backgroundLandingPage.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center bottom',
            backgroundRepeat: 'no-repeat',
            opacity: 0.35,
          }}
        />
        <div className="pointer-events-none absolute inset-0 z-0" style={{ background: 'linear-gradient(to bottom, rgba(10,6,30,0.55) 0%, rgba(10,6,30,0.75) 100%)' }} />

        <div className="relative z-10 mx-auto max-w-7xl px-6 py-12">
          <div className="grid lg:grid-cols-4 gap-10">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <AntaresLogo className="h-20 w-auto" />
              </div>
              <p className="text-sm text-white/65 max-w-xl">
                Transformamos tecnologia em crescimento real. Automação, sistemas sob medida e soluções inteligentes para empresas que querem vender mais e escalar com segurança.
              </p>

              {/* Importante: informações legais visíveis para verificação da Meta */}
              <div className="mt-6 rounded-xl border border-white/15 bg-white/8 p-4" style={{ background: 'rgba(255,255,255,0.07)' }}>
                <p className="mt-2 text-sm text-white/90">
                  <strong>{LEGAL_NAME}</strong>
                </p>
                <p className="mt-1 text-sm text-white/75">
                  CNPJ: <strong className="text-white/90">{LEGAL_CNPJ}</strong>
                </p>
                <p className="mt-1 text-sm text-white/75">
                  Razão Social: <strong className="text-white/90">{LEGAL_RAZAO_SOCIAL}</strong>
                </p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-3">Links</h3>
              <ul className="space-y-2 text-sm text-white/65">
                <li><a href="#servicos" className="hover:text-white transition">Serviços</a></li>
                <li><a href="#produtos" className="hover:text-white transition">Produtos</a></li>
                <li><a href="#experiencia" className="hover:text-white transition">Portfólio</a></li>
                <li><a href="#diferenciais" className="hover:text-white transition">Diferenciais</a></li>
                <li><a href="#faq" className="hover:text-white transition">Dúvidas</a></li>
                <li><Link to="/termos" className="hover:text-white transition">Termos de Uso</Link></li>
                <li><Link to="/privacidade" className="hover:text-white transition">Política de Privacidade</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-3">Contato</h3>
              <ul className="space-y-2 text-sm text-white/65">
                <li className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-purple-400" />
                  <a href={WHATSAPP} className="hover:text-white transition">WhatsApp</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-purple-400" />
                  <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white transition">{CONTACT_EMAIL}</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-white/15 text-center text-sm text-white/50">
            <p>© {year} {BRAND_NAME}. Todos os direitos reservados.</p>
            <p className="mt-2">Integrações oficiais WhatsApp Business API • Twilio • 360dialog</p>
          </div>
        </div>
      </footer>
      <AntaresAIChat />
    </main>
  );
}

function TrustChip({ icon: Icon, title, desc }: { icon: any; title: string; desc: string }) {
  return (
    <div className="rounded-xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-sm">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-purple-300" />
        <span className="font-semibold text-white">{title}</span>
      </div>
      <p className="mt-1 text-xs text-white/60">{desc}</p>
    </div>
  );
}

function BenefitLine({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary/20 text-brand-primary">
        <span className="text-xs">✓</span>
      </span>
      <p className="text-sm text-brand-muted">{children}</p>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-brand-border bg-brand-card px-4 py-3">
      <p className="text-xs text-brand-muted">{label}</p>
      <p className="mt-1 font-semibold text-brand-text">{value}</p>
    </div>
  );
}

function StepCard({ step, title, desc }: { step: string; title: string; desc: string }) {
  return (
    <div className="rounded-2xl border border-brand-border bg-white p-6 hover:shadow-md transition shadow-sm">
      <p className="text-xs tracking-widest text-brand-muted">{step}</p>
      <p className="mt-2 text-lg font-bold text-brand-text">{title}</p>
      <p className="mt-2 text-sm text-brand-muted">{desc}</p>
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <div className="rounded-2xl border border-brand-border bg-white p-6 shadow-sm">
      <p className="font-semibold text-brand-text">{q}</p>
      <p className="mt-2 text-sm text-brand-muted">{a}</p>
    </div>
  );
}

function ContactCard({ icon: Icon, title, value, href, hint }: { icon: any; title: string; value: string; href?: string; hint?: string }) {
  const body = (
    <div className="rounded-2xl border border-brand-border bg-white p-5 hover:shadow-md transition shadow-sm">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-brand-primary" />
        <p className="text-sm font-semibold text-brand-text">{title}</p>
      </div>
      <p className="mt-3 text-sm text-brand-text break-all">{value}</p>
      {hint ? <p className="mt-1 text-xs text-brand-muted">{hint}</p> : null}
    </div>
  );

  return href ? (
    <a href={href} className="block">{body}</a>
  ) : (
    body
  );
}